class TagScroller {
    _root: HTMLElement;
    _parentElement: HTMLElement;
    _btnPrev: HTMLButtonElement;
    _btnNext: HTMLButtonElement;
    _resizeDebounceTimeout: number;
    _scrollDistance = 50;
    _visibilityClass = 'tagscroller__btn--hidden';

    constructor( element: HTMLElement ) {
        this._root = element;

        if ( !this._root.parentElement ) {
            throw new Error( 'cannot create tag scoller without parent element' );
        }

        this._root.dataset.tagScrollerInitialised = 'true';

        this._parentElement = this._root.parentElement;
        this._btnPrev = this._parentElement.querySelector( '.tagscroller__btn--prev' ) as HTMLButtonElement;
        this._btnNext = this._parentElement.querySelector( '.tagscroller__btn--next' ) as HTMLButtonElement;

        this._root.addEventListener( 'scroll', this._onScroll.bind( this ), { passive: true } );
        this._btnPrev.addEventListener( 'click', this._onClickPrevious.bind( this ) );
        this._btnNext.addEventListener( 'click', this._onClickNext.bind( this ) );

        window.addEventListener( 'resize', this._onResize.bind( this ) );

        this.toggle();
    }

    activate(): void {
        this._btnNext.classList.remove( this._visibilityClass );

        const lastTag = this._root.querySelector( '.last' ) as HTMLElement;

        if ( lastTag ) {
            lastTag.style.marginRight = '20px';
        }

        this._root.style.overflowX = 'scroll';
    }

    deactivate(): void {
        this._btnPrev.classList.add( this._visibilityClass );
        this._btnNext.classList.add( this._visibilityClass );
        this._root.style.overflowX = '';

        const lastTag = this._root.querySelector( '.last' ) as HTMLElement;

        if ( lastTag ) {
            lastTag.style.marginRight = '0';
        }
    }

    toggle(): void {
        if ( this._isScrollable() ) {
            this.activate();
        } else {
            this.deactivate();
        }
    }

    _isScrollable(): boolean {
        const rect = this._parentElement.getBoundingClientRect();
        return !( this._root.scrollWidth <= rect.width );
    }

    _onResize( event: Event ): void {
        if ( this._resizeDebounceTimeout ) {
            window.clearTimeout( this._resizeDebounceTimeout );
        }
        this._resizeDebounceTimeout = window.setTimeout( this.toggle.bind( this ), 500 );
    }

    _onScroll( event: Event ): void {
        if ( this._root.scrollLeft === 0 ) {
            this._btnPrev.classList.add( this._visibilityClass );
        }

        if ( this._root.scrollLeft > 0 ) {
            this._btnPrev.classList.remove( this._visibilityClass );
        }

        const rect = this._parentElement.getBoundingClientRect();
        const limit = this._root.scrollLeft + rect.width;

        if ( this._root.scrollLeft < limit ) {
            this._btnNext.classList.remove( this._visibilityClass );
        }

        if ( this._root.scrollWidth === limit ) {
            this._btnNext.classList.add( this._visibilityClass );
        }
    }

    _onClickPrevious( event: PointerEvent ): void {
        this._root.scrollLeft -= this._scrollDistance;

        this._btnPrev.classList.remove( this._visibilityClass );

        if ( this._root.scrollLeft === 0 ) {
            this._btnPrev.classList.add( this._visibilityClass );
        }
    }

    _onClickNext( event: PointerEvent ): void {
        this._btnPrev.classList.remove( this._visibilityClass );

        this._root.scrollLeft += this._scrollDistance;

        const rect = this._parentElement.getBoundingClientRect();

        if ( this._root.scrollWidth === ( this._root.scrollLeft + rect.width ) ) {
            this._btnNext.classList.add( this._visibilityClass );
        }
    }
}

export default function initTagScrollers( selector?: string ): void {
    function init() {
        document.querySelectorAll<HTMLElement>( selector || '.property__tags .tags' ).forEach( ( el: HTMLElement ) => {
            if ( el.dataset.tagScrollerInitialised ) {
                return;
            }
            new TagScroller( el );
        } );
    }

    window.addEventListener( 'twc:popup:open-property', init );

    init();
}
